import { createContext, useContext, ReactNode, useMemo } from 'react'
import { useCounts } from '@novu/react'

interface NotificationCountContextValue {
  unreadCount: number
  unseenCount: number
  isLoading: boolean
  refetch: () => void
}

const NotificationCountContext = createContext<NotificationCountContextValue | undefined>(undefined)

interface NotificationCountProviderProps {
  children: ReactNode
}

// This component must be used inside NovuProvider
export function NotificationCountProviderWithNovu({ children }: NotificationCountProviderProps) {
  // Memoize filters to prevent unnecessary re-subscriptions
  const filters = useMemo(() => [{ read: false }], [])

  // Use Novu's useCounts hook - this works inside NovuProvider
  // Pass filters to get unread count
  const countsResult = useCounts({
    filters
  })

  // Extract counts
  const unreadCount = countsResult.counts?.[0]?.count || 0
  const unseenCount = countsResult.counts?.[0]?.count || 0 // Use same value for now
  const isLoading = countsResult.isLoading || false
  const refetch = countsResult.refetch || (() => {})

  // Memoize the context value to prevent infinite loops
  const value = useMemo(
    () => ({
      unreadCount,
      unseenCount,
      isLoading,
      refetch
    }),
    [unreadCount, unseenCount, isLoading, refetch]
  )

  return (
    <NotificationCountContext.Provider value={value}>
      {children}
    </NotificationCountContext.Provider>
  )
}

// Fallback provider when user is not logged in
export function NotificationCountProviderFallback({ children }: NotificationCountProviderProps) {
  // Memoize the static fallback value
  const value = useMemo(
    () => ({
      unreadCount: 0,
      unseenCount: 0,
      isLoading: false,
      refetch: () => {}
    }),
    []
  )

  return (
    <NotificationCountContext.Provider value={value}>
      {children}
    </NotificationCountContext.Provider>
  )
}

export function useNotificationCount() {
  const context = useContext(NotificationCountContext)
  if (!context) {
    throw new Error('useNotificationCount must be used within a NotificationCountProvider')
  }
  return context
}
